import React, { createRef, useMemo, useEffect, useContext } from 'react';
import Slider from 'react-slick';
import ReactHtmlParser from 'react-html-parser';
import { debounce as _debounce } from 'lodash';
import Button from '@latitude/button-rebrand';
import Typography from '@latitude/typography';
import {
  StyledCardCollectionCarousel,
  StyledCard,
  StyledCardCollectionContainer,
  StyledCardCollectionImgContainer,
  StyledCardDescription,
  StyledCardCollectionContentContainer,
  StyledCardCollectionCTAWrapper,
  StyledHeadingContainer,
  StyledSliderContainer,
  StyledCardHeading
} from './CardCollectionRebrand.styled';
import { REBRAND_COLOR } from '@latitude/core/utils/constants';

const COMPONENT_ID = 'card-collection-rebrand';

const CardCollection = ({
  title,
  cards,
  trackingId,
  background
}) => {

  const componentIdentifier = trackingId
    ? `${trackingId}-${COMPONENT_ID}`
    : COMPONENT_ID;
  const sliderSettings = {
    dots: true,
    speed: 500,
    arrows: true,
    slidesToShow: 6,
    infinite: false,
    variableWidth: true,
    nextArrow: false,
    prevArrow: false,
    responsive: [
      {
        breakpoint: 680,
        settings: { slidesToShow: 1 }
      },
      {
        breakpoint: 1020,
        settings: { slidesToShow: 2 }
      },
      {
        breakpoint: 1350,
        settings: { slidesToShow: 3 }
      },
      {
        breakpoint: 1670,
        settings: { slidesToShow: 4 }
      },
      {
        breakpoint: 3520,
        settings: { slidesToShow: 5 }
      }
    ]
  };

  const cardsTitleRef = useMemo(() => {
    const refs = {};
    cards?.forEach((card, index) => {
      refs[card.id] = createRef();
    });
    return refs;
  }, [cards]);

  const setTitleHeight = height => {
    Object.keys(cardsTitleRef).forEach(key => {
      const ref = cardsTitleRef[key];
      if (ref && ref.current) {
        ref.current.style.minHeight = height ? `${height}px` : 'auto';
      }
    });
  };

  const equalizeHeadingHeight = () => {
    let max = 0;
    Object.keys(cardsTitleRef).forEach(key => {
      const element = cardsTitleRef[key]?.current;
      if (element && element.offsetHeight > max) {
        max = element.offsetHeight;
      }
    });
    setTitleHeight(max);
  };

  const cardsDisclaimerRef = useMemo(() => {
    const refs = {};
    cards?.forEach((card, index) => {
      refs[card.id] = createRef();
    });
    return refs;
  }, [cards]);

  const cardsBodyRef = useMemo(() => {
    const refs = {};
    cards?.forEach((card, index) => {
      refs[card.id] = createRef();
    });
    return refs;
  }, [cards]);

  const setBodyHeight = height => {
    Object.keys(cardsBodyRef).forEach(key => {
      const ref = cardsBodyRef[key];
      if (ref && ref.current) {
        ref.current.style.minHeight = height ? `${height}px` : 'auto';
      }
    });
  };

  const equalizeBodyHeight = () => {
    let max = 0;
    Object.keys(cardsBodyRef).forEach(key => {
      const element = cardsBodyRef[key]?.current;
      if (element && element.offsetHeight > max) {
        max = element.offsetHeight;
      }
    });
    setBodyHeight(max);
  };

  const cardsRef = useMemo(() => {
    const refs = {};
    cards?.forEach((card, index) => {
      refs[card.id] = createRef();
    });
    return refs;
  }, [cards]);

  const setCardHeight = height => {
    Object.keys(cardsRef).forEach(key => {
      const ref = cardsRef[key];
      if (ref && ref.current) {
        ref.current.style.minHeight = height ? `${height - 50}px` : 'auto';
      }
    });
  };

  const equalizeCardHeight = () => {
    let max = 0;
    Object.keys(cardsRef).forEach(key => {
      const element = cardsRef[key]?.current;
      if (element && element.offsetHeight > max) {
        max = element.offsetHeight;
      }
    });
    setCardHeight(max);
  };

  const handleWindowResize = () => {
    setCardHeight();
    equalizeCardHeight();
    setTitleHeight();
    equalizeHeadingHeight();
    setBodyHeight();
    equalizeBodyHeight();
  };

  const debouncedHandleResize = _debounce(handleWindowResize, 100);

  useEffect(() => {
    equalizeCardHeight();
    equalizeHeadingHeight();
    equalizeBodyHeight();
    window.addEventListener('resize', debouncedHandleResize);
    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, [
    cardsTitleRef,
    cardsRef,
    cardsBodyRef,
    cardsDisclaimerRef,
    cards
  ]);

  useEffect(() => {
    debouncedHandleResize();
  }, []);

  return (
    <StyledCardCollectionCarousel
      className={componentIdentifier}
      $componentIdentifier={componentIdentifier}
      $background={background}
    >
      <StyledHeadingContainer
        className={`${componentIdentifier}-heading-container`}
      >
        <Typography.H4>{title}</Typography.H4>
      </StyledHeadingContainer>

      <StyledSliderContainer
        className={`${componentIdentifier}-slider-container`}
      >
        <Slider {...sliderSettings}>
          {cards?.map(card => (
            <StyledCard
              ref={cardsRef[card.id]}
              key={card?.id}
              className={`${componentIdentifier}-card`}
            >
              <CardContent
                cardsTitleRef={cardsTitleRef}
                cardsBodyRef={cardsBodyRef}
                card={card}
                componentIdentifier={componentIdentifier}
              />
            </StyledCard>
          ))}
        </Slider>
      </StyledSliderContainer>
    </StyledCardCollectionCarousel>
  );
};

const CardContent = ({
  cardsTitleRef,
  cardsBodyRef,
  card,
  componentIdentifier
}) => {

  return (
    <StyledCardCollectionContainer>
      <StyledCardCollectionImgContainer
        className={`${componentIdentifier}-card-img-container`}
      >
        <img src={card?.image} alt={card?.title} />
      </StyledCardCollectionImgContainer>

      <StyledCardCollectionContentContainer
        className={`${componentIdentifier}-card-content-container`}
      >
        <StyledCardHeading
          ref={cardsTitleRef[card.id]}
          className={`${componentIdentifier}-card-heading`}
        >
          <Typography.SH4 semibold color={REBRAND_COLOR.BLACK}>{card?.title}</Typography.SH4>
        </StyledCardHeading>
        <StyledCardDescription
          ref={cardsBodyRef[card.id]}
          className={`${componentIdentifier}-card-description`}
        >
          <Typography.Body color={REBRAND_COLOR.BLACK_80}>
            {ReactHtmlParser(card.description)}
          </Typography.Body>
        </StyledCardDescription>

        <StyledCardCollectionCTAWrapper
          className={`${componentIdentifier}-card-cta-wrapper`}
        >
          {card?.button && (
            <Button
              variant={card?.button?.variant}
              size={card?.button?.size}
              className={`${componentIdentifier}-button`}
              ctaText={card?.button?.text}
              ctaUrl={card?.button?.url}
              trackEventData={{
                category: 'text-link',
                location: `card-collection-${card?.title}`,
                label: card?.button?.text
              }}
              trackId={COMPONENT_ID}
            />
          )}
        </StyledCardCollectionCTAWrapper>
      </StyledCardCollectionContentContainer>
    </StyledCardCollectionContainer>
  );
};

export default CardCollection;
